/**
 * Génération des trois types de snippets d'intégration d'un générateur Quotio
 * sur un site tiers :
 *
 *   1. link    — simple lien `<a href="…">` vers le générateur public.
 *   2. iframe  — balise `<iframe>` avec auto-resize via postMessage.
 *   3. widget  — script `embed.js` qui injecte l'iframe dans un `<div>` marqué.
 *
 * Les snippets sont purement textuels (pas d'injection DOM).
 */

export type EmbedMode = 'inline' | 'popup';

export interface EmbedOptions {
    /** URL absolue du générateur public — sans paramètres. */
    publicUrl: string;
    /** Mode d'affichage pour l'iframe / le widget. */
    mode: EmbedMode;
    /** Hauteur minimale initiale en px (sera ajustée automatiquement). */
    minHeight?: number;
    /** Libellé du bouton déclencheur (mode popup uniquement). */
    buttonLabel?: string;
}

/**
 * Construit l'URL d'embed avec le bon paramètre.
 */
export function buildEmbedUrl(publicUrl: string): string {
    const sep = publicUrl.includes('?') ? '&' : '?';
    return `${publicUrl}${sep}embed=1`;
}

/* ================================================================
   1) Snippet lien direct (le plus simple)
   ================================================================ */
export function buildLinkSnippet(opts: EmbedOptions): string {
    const url = opts.publicUrl;
    return `<a href="${url}" target="_blank" rel="noopener" style="display:inline-block;padding:12px 24px;background:#0f172a;color:#fff;border-radius:10px;text-decoration:none;font-weight:600;font-family:system-ui,sans-serif">
  Demander un devis en ligne
</a>`;
}

/* ================================================================
   2) Snippet iframe (auto-resize via postMessage)
   ================================================================ */
export function buildIframeSnippet(opts: EmbedOptions): string {
    const embedUrl = buildEmbedUrl(opts.publicUrl);
    const minHeight = opts.minHeight ?? 720;
    const origin = safeOrigin(opts.publicUrl);

    return `<!-- Quotio · générateur de devis -->
<iframe
  id="quotio-generator"
  src="${embedUrl}"
  title="Demandez votre devis en ligne"
  style="width:100%;min-height:${minHeight}px;border:0;display:block;background:transparent"
  loading="lazy"
  allow="clipboard-write"
></iframe>
<script>
(function () {
  var EXPECTED_ORIGIN = ${JSON.stringify(origin)};
  window.addEventListener('message', function (e) {
    if (EXPECTED_ORIGIN && e.origin !== EXPECTED_ORIGIN) return;
    var data = e.data;
    if (!data || typeof data !== 'object') return;
    if (data.type !== 'quotio:resize') return;
    var iframe = document.getElementById('quotio-generator');
    if (!iframe) return;
    var h = Math.max(parseInt(data.height, 10) || 0, 320);
    iframe.style.height = h + 'px';
  }, false);
})();
</script>`;
}

/* ================================================================
   3) Snippet widget (embed.js)
   ================================================================ */
export function buildWidgetSnippet(opts: EmbedOptions): string {
    const origin = safeOrigin(opts.publicUrl);
    const scriptUrl = `${origin}/embed.js`;
    const embedUrl = buildEmbedUrl(opts.publicUrl);

    if (opts.mode === 'popup') {
        const label = opts.buttonLabel ?? 'Obtenir un devis';
        return `<!-- Quotio · bouton flottant -->
<script
  async
  src="${scriptUrl}"
  data-quotio-src="${embedUrl}"
  data-quotio-mode="popup"
  data-quotio-label="${escapeAttr(label)}"
></script>`;
    }

    // Mode inline : on laisse le dev placer un div où il veut
    return `<!-- Quotio · générateur inline -->
<div data-quotio-embed="${embedUrl}" style="min-height:${opts.minHeight ?? 720}px"></div>
<script async src="${scriptUrl}"></script>`;
}

/* ================================================================
   Helpers
   ================================================================ */
function safeOrigin(url: string): string {
    try {
        return new URL(url).origin;
    } catch {
        return '';
    }
}

function escapeAttr(v: string): string {
    return v.replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
